import { usePrincipal } from '@/common/principal/PrincipalContext';
import { usePushStream } from '@/common/push/PushStreamContext';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * This context allows components to "subscribe" to changes to entities. Subscriptions are registered
 * with the owning module over REST and tied to the browser's push stream.
 *
 * Each subscription is identified by a unique subscrUuid. When the backend
 * notifies the frontend, it sends a set of subscrUuids that fired.
 * The context invokes the registered callback for each fired subscrUuid.
 */
const EntityChangeContext = createContext({
  subscrToEntityChange: () => {},
  unsubscrToEntityChange: () => {},
  blockRefreshTrigger: false,
  toggleBlockRefreshTrigger: () => {},
});

export const EntityChangeProvider = ({ children }) => {
  const { getToken, org } = usePrincipal();
  const { streamUuid, addListener } = usePushStream();

  const [blockRefreshTrigger, setBlockRefreshTrigger] = useState(false);
  const blockRefreshTriggerRef = useRef(blockRefreshTrigger);

  useEffect(() => {
    blockRefreshTriggerRef.current = blockRefreshTrigger;
  }, [blockRefreshTrigger]);

  const toggleBlockRefreshTrigger = useCallback(() => {
    setBlockRefreshTrigger((prev) => !prev);
  }, []);

  // subscrUuid -> { module, entity, expr, org, callback }
  const subscrsRef = useRef(new Map());

  // Not RsrcContext: a failed subscription should not alert the user or block resyncs
  const post = (subscr, endpoint, body, keepalive) =>
    fetch(`/${subscr.module}/api/rest/${subscr.org}/${subscr.entity}/${endpoint}`, {
      method: 'POST',
      keepalive,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) {
          console.warn(`[EntityChange] ${endpoint} ${subscr.module}/${subscr.entity} responded ${res.status}`);
        }
      })
      .catch((error) => console.warn(`[EntityChange] ${endpoint} ${subscr.module}/${subscr.entity} failed`, error));

  const postSubscr = (subscrUuid, subscr) =>
    post(subscr, 'entityChangeSubscr', { streamUuid, subscrUuid, expr: subscr.expr }, false);

  const postUnsubscr = (subscrUuid, subscr, keepalive) =>
    post(subscr, 'entityChangeUnsubscr', { subscrUuid }, keepalive);

  const subscrToEntityChange = (module, entity, subscrUuid, expr, callback) => {
    const subscr = { module, entity, expr, org, callback };
    subscrsRef.current.set(subscrUuid, subscr);
    postSubscr(subscrUuid, subscr);
  };

  const unsubscrToEntityChange = (module, entity, subscrUuid) => {
    const subscr = subscrsRef.current.get(subscrUuid);
    if (!subscr) {
      return;
    }
    subscrsRef.current.delete(subscrUuid);
    postUnsubscr(subscrUuid, subscr, false);
  };

  const fire = (subscrUuids) => {
    if (blockRefreshTriggerRef.current) {
      return;
    }
    subscrUuids.forEach((subscrUuid) => subscrsRef.current.get(subscrUuid)?.callback());
  };

  /**
   * Re-sends subscriptions a module may have lost (stream reconnected, module restarted, page restored).
   * Views are refreshed when changes may have been missed in the meantime.
   */
  const resync = (module, refresh) => {
    const resynced = [];
    subscrsRef.current.forEach((subscr, subscrUuid) => {
      if (module && subscr.module !== module) {
        return;
      }
      postSubscr(subscrUuid, subscr);
      resynced.push(subscrUuid);
    });
    if (refresh) {
      fire(resynced);
    }
  };

  useEffect(() => {
    const removeListeners = [
      addListener('entity-change', ({ subscrUuids }) => fire(subscrUuids)),
      addListener('connected', ({ resumed }) => resync(null, resumed)),
      addListener('module-up', ({ module }) => resync(module, true)),
    ];

    // The stream is shared with other tabs and outlives this one, so its subscriptions must be removed
    const onPageHide = () => subscrsRef.current.forEach((subscr, subscrUuid) => postUnsubscr(subscrUuid, subscr, true));
    const onPageShow = (event) => event.persisted && resync(null, true);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      removeListeners.forEach((removeListener) => removeListener());
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [addListener]);

  return (
    <EntityChangeContext.Provider
      value={{ subscrToEntityChange, unsubscrToEntityChange, blockRefreshTrigger, toggleBlockRefreshTrigger }}
    >
      {children}
    </EntityChangeContext.Provider>
  );
};

/**
 * Hook for components to subscribe to entity changes.
 * Returns a refreshTrigger that toggles only when this specific subscription fires.
 *
 * @param {string} module - The backend module (e.g. 'gp', 'gtn')
 * @param {string} entity - The entity name (e.g. 'CUST', 'CTRT')
 * @param {object} [expr] - Optional Expr filter to narrow the subscription scope
 */
export const useEntityChangeSubscr = (module, entity, expr) => {
  const { subscrToEntityChange, unsubscrToEntityChange } = useContext(EntityChangeContext);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const subscrUuidRef = useRef(crypto.randomUUID());

  useEffect(() => {
    const subscrUuid = subscrUuidRef.current;
    const callback = () => setRefreshTrigger((prev) => !prev);

    subscrToEntityChange(module, entity, subscrUuid, expr, callback);

    return () => {
      unsubscrToEntityChange(module, entity, subscrUuid);
    };
  }, [module, entity]);

  return { refreshTrigger };
};

export const useEntityChange = () => useContext(EntityChangeContext);
